import {
  useGetCallTranscript,
  useGetTwilioCallDetails,
} from '@hakimo-ui/hakimo/data-access';
import { QueryResult } from '@hakimo-ui/hakimo/ui-elements';
import { TwilioCalDetailsDTO } from '@hakimo-ui/hakimo/types';
import { getTwilioCallErrorFormatter } from './extraDetailsUtil';
import { InlineCallTranscript } from './InlineCallTranscript';
import { useRef, useState } from 'react';

const REFETCH_INTERVAL = 10000;

interface Props {
  twilioSsid: string;
  twilioCallingToName?: string;
  userName?: string;
  alarmGroupId?: string;
  locationAlarmId?: string;
}

export const TwilioCallDetails = (props: Props) => {
  const {
    twilioSsid,
    twilioCallingToName,
    userName,
    alarmGroupId,
    locationAlarmId,
  } = props;
  const hasRetriedForMediaRef = useRef(false);
  const [isTranscriptRequested, setIsTranscriptRequested] = useState(false);

  const getMediaRefetchInterval = (
    data: TwilioCalDetailsDTO | undefined
  ): number | false => {
    if (!data) {
      return REFETCH_INTERVAL;
    }
    if (
      data.endTime === 0 ||
      (data.endTime > 0 && !data.mediaUrl && hasRetriedForMediaRef.current)
    ) {
      return false;
    }
    if (data.endTime > 0 && !data.mediaUrl) {
      hasRetriedForMediaRef.current = true;
    }
    return REFETCH_INTERVAL;
  };

  const query = useGetTwilioCallDetails(twilioSsid, getMediaRefetchInterval);
  const transcriptQuery = useGetCallTranscript(
    twilioSsid,
    alarmGroupId,
    locationAlarmId,
    isTranscriptRequested
  );

  return (
    <div>
      <QueryResult
        queryResult={query}
        loadingText="Loading call details"
        errorFormatter={getTwilioCallErrorFormatter()}
      >
        {(data) => {
          return (
            <div>
              <div>
                {userName ? `${userName} made a call to ` : 'Call was made to '}
                {twilioCallingToName
                  ? `${twilioCallingToName}(${data.callTo || '-'})`
                  : data.callTo || '-'}
              </div>
              {data.mediaUrl ? (
                <>
                  <audio
                    preload="auto"
                    src={data.mediaUrl}
                    className="h-7"
                    controls
                  ></audio>
                  <InlineCallTranscript
                    isRequested={isTranscriptRequested}
                    isLoading={transcriptQuery.isLoading}
                    transcript={transcriptQuery.data?.transcript}
                    onRequest={() => setIsTranscriptRequested(true)}
                  />
                </>
              ) : (
                'Call recording not found.'
              )}
            </div>
          );
        }}
      </QueryResult>
    </div>
  );
};

export const getCallDetailsNode = (
  ssid: string,
  twilioCallingToName?: string,
  userName?: string,
  alarmGroupId?: string,
  locationAlarmId?: string
) => {
  return (
    <TwilioCallDetails
      twilioSsid={ssid}
      twilioCallingToName={twilioCallingToName}
      userName={userName}
      alarmGroupId={alarmGroupId}
      locationAlarmId={locationAlarmId}
    />
  );
};
